import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Modal,
  Input,
  Table,
  Menu,
  Dropdown,
  Tag,
  Switch,
  Tooltip,
} from "antd";
import { useHistory } from "react-router-dom";
import {
  RiEditFill,
  RiDeleteBin2Fill,
  RiStarFill,
  RiEyeLine,
  RiEyeOffLine,
} from "react-icons/ri";
import {
  getAllCopilotChatsPaginatedSearch,
  deleteCopilotChatById,
  updateCopilotChatById,
} from "@api/copilots/browser-extension/chat/copilotChat";
import {
  SuccessNotification,
  ErrorNotification,
  CopilotChatCreateModal,
  CopilotChatUpdateModal,
} from "@components";
import { IoAddSharp } from "react-icons/io5";

const { Search } = Input;

const CopilotChatsList = () => {
  const history = useHistory();
  const [copilotChats, setCopilotChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [copilotChatId, setCopilotChatId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchCopilotChats();
  }, [pagination.current, pagination.pageSize]);

  const fetchCopilotChats = async () => {
    setLoading(true);
    const { data } = await getAllCopilotChatsPaginatedSearch(
      pagination.current,
      pagination.pageSize,
      searchTerm
    );
    setCopilotChats(data.copilotChats);
    setPagination((p) => ({
      ...p,
      total: data.paginationData.totalCopilotChats,
      current: data.paginationData.page,
      pageSize: data.paginationData.perPage,
    }));
    setLoading(false);
  };

  // Handle create copilotChat modal
  const handleCreateModalClick = () => {
    setCreateModalOpen(!createModalOpen);
  };

  // Handle update copilotChat modal
  const handleUpdateModalClick = (id) => {
    setCopilotChatId(id);
    setUpdateModalOpen(!updateModalOpen);
  };

  // Handle copilotChat delete
  const handleCopilotChatDelete = (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this copilot chat?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteCopilotChatById(id);
          fetchCopilotChats();
          SuccessNotification("Copilot Chat deleted successfully");
        } catch (error) {
          ErrorNotification("Error deleting copilot chat");
        }
      },
    });
  };

  // Handle toggle publish status
  const handleTogglePublish = async (id, publish) => {
    try {
      await updateCopilotChatById(id, { publish });
      fetchCopilotChats();
      SuccessNotification(`Copilot ${publish ? "published" : "unpublished"} successfully`);
    } catch (error) {
      ErrorNotification("Error updating copilot status");
    }
  };

  // Handle toggle featured status
  const handleToggleFeatured = async (id, featured) => {
    try {
      await updateCopilotChatById(id, { featured });
      fetchCopilotChats();
      SuccessNotification(`Copilot ${featured ? "featured" : "unfeatured"} successfully`);
    } catch (error) {
      ErrorNotification("Error updating copilot status");
    }
  };

  // Dropdown menu for additional actions
  const menu = (
    <Menu>
      {/* Copilot Chat Categories */}
      <Menu.Item
        key="0"
        onClick={() => {
          history.push("/super-admin/copilots/browser-extension/copilot-chat-categories");
        }}
      >
        Copilot Chat Categories
      </Menu.Item>

      {/* Browser Copilots Categories */}
      <Menu.Item
        key="1"
        onClick={() => {
          history.push("/super-admin/copilots/browser-extension/categories");
        }}
      >
        Browser Copilots Categories
      </Menu.Item>

      {/* Browser Copilots */}
      <Menu.Item
        key="2"
        onClick={() => {
          history.push("/super-admin/copilots/browser-extension/copilots");
        }}
      >
        Browser Copilots
      </Menu.Item>

      {/* Mobile App Copilot Chats Categories */}
      <Menu.Item
        key="3"
        onClick={() => {
          history.push("/super-admin/copilots/mobile-app/categories");
        }}
      >
        Mobile App Copilot Chat Categories
      </Menu.Item>

      {/* Mobile App Copilot Chats */}
      <Menu.Item
        key="4"
        onClick={() => {
          history.push("/super-admin/copilots/mobile-app/copilot-chats");
        }}
      >
        Mobile App Copilot Chats
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "#",
      dataIndex: "order",
      key: "order",
      width: 60,
      sorter: (a, b) => a.order - b.order,
    },
    {
      title: "Copilot",
      key: "name",
      width: 280,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {record.image ? (
            <img
              src={record.image}
              alt={record.name}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                objectFit: "cover",
              }}
            />
          ) : (
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                backgroundColor: record.color || "#6366f1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
              }}
            >
              {record.icon || "📊"}
            </div>
          )}
          <div>
            <div style={{ fontWeight: 500 }}>{record.name}</div>
            <div
              style={{
                fontSize: "12px",
                color: "#666",
                maxWidth: "180px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {record.shortDescription || record.description}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 140,
      render: (category) =>
        category ? (
          <Tag color="blue">
            {category.icon} {category.name}
          </Tag>
        ) : (
          <span style={{ color: "#999" }}>Uncategorized</span>
        ),
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      width: 140,
      render: (model) => (
        <Tag color={model?.includes("gpt-4") ? "green" : "default"}>
          {model || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Plans",
      dataIndex: "plans",
      key: "plans",
      width: 120,
      render: (plans) => (
        <div>
          {plans?.slice(0, 2).map((plan) => (
            <Tag key={plan} style={{ marginBottom: 2 }}>
              {plan}
            </Tag>
          ))}
          {plans?.length > 2 && <Tag>+{plans.length - 2}</Tag>}
        </div>
      ),
    },
    {
      title: "Stats",
      key: "stats",
      width: 120,
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>
          <div>Used: {record.used || 0}</div>
          <div>Likes: {record.likes?.length || 0}</div>
        </div>
      ),
    },
    {
      title: "Featured",
      dataIndex: "featured",
      key: "featured",
      width: 90,
      render: (featured, record) => (
        <Tooltip title={featured ? "Remove from featured" : "Mark as featured"}>
          <Switch
            checked={featured}
            onChange={(checked) => handleToggleFeatured(record._id, checked)}
            size="small"
            checkedChildren={<RiStarFill />}
          />
        </Tooltip>
      ),
    },
    {
      title: "Published",
      dataIndex: "publish",
      key: "publish",
      width: 100,
      render: (publish, record) => (
        <Tooltip title={publish ? "Unpublish" : "Publish"}>
          <Switch
            checked={publish}
            onChange={(checked) => handleTogglePublish(record._id, checked)}
            size="small"
            checkedChildren={<RiEyeLine />}
            unCheckedChildren={<RiEyeOffLine />}
          />
        </Tooltip>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <span>
          <RiEditFill
            style={{ marginRight: 12, cursor: "pointer", color: "#1890ff" }}
            onClick={() => handleUpdateModalClick(record._id)}
          />
          <RiDeleteBin2Fill
            style={{ cursor: "pointer", color: "#ff4d4f" }}
            onClick={() => handleCopilotChatDelete(record._id)}
          />
        </span>
      ),
    },
  ];

  const handlePaginationChange = (pagination) => {
    setPagination(pagination);
  };

  return (
    <>
      {/* Create CopilotChat Modal */}
      {createModalOpen && (
        <CopilotChatCreateModal
          open={createModalOpen}
          handleModalClick={handleCreateModalClick}
          fetchCopilotChats={fetchCopilotChats}
        />
      )}

      {/* Update CopilotChat Modal */}
      {updateModalOpen && (
        <CopilotChatUpdateModal
          open={updateModalOpen}
          handleModalClick={handleUpdateModalClick}
          fetchCopilotChats={fetchCopilotChats}
          copilotChatId={copilotChatId}
        />
      )}

      <Card style={{ borderRadius: "0", height: "100vh" }}>
        <Row>
          <Col span={24}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <h3 style={{ margin: 0 }}>Browser - Copilot Chats List</h3>
                <p style={{ color: "#666", marginTop: 4, marginBottom: 0, fontSize: 13 }}>
                  Manage AI assistants for the browser extension
                </p>
              </Col>

              <Col>
                <Search
                  placeholder="Search by name, category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onSearch={fetchCopilotChats}
                  style={{ width: "250px", marginRight: 16 }}
                />

                <Button
                  type="primary"
                  onClick={handleCreateModalClick}
                  style={{ marginRight: 8 }}
                >
                  Create Copilot Chat
                </Button>

                <Dropdown overlay={menu} placement="bottomRight">
                  <Button icon={<IoAddSharp />} />
                </Dropdown>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <Table
                  columns={columns}
                  dataSource={copilotChats}
                  pagination={pagination}
                  loading={loading}
                  onChange={handlePaginationChange}
                  rowKey={(record) => record._id}
                  scroll={{ y: "calc(100vh - 270px)", x: "max-content" }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default CopilotChatsList;
